import { Injectable } from '@angular/core';
import { GenericService, LoaderConfig, ErrorConfig } from './generic.service';
import { environment } from 'src/environments/environment';
import { catchError, concatMap, EMPTY, map, Observable, of } from 'rxjs';
import { ServerResponse } from '../models/ServerResponse';
import { CreateEntity, Entity, EntityExistenceAndPermissions } from '../models/Entity';
import { ISAPI, ISPagination } from 'is-lib';
import { UsersService } from './users.service';
import { UserProfile } from '../models/UserProfile';
import { AuthService } from './auth.service';
import { RequestCacheConfig } from '../models/Request';

@Injectable({
    providedIn: 'root'
})
export class EntitiesService {
    constructor(private _httpService: GenericService, private _user: UsersService, private _auth: AuthService) {}

    getEntity(id: string, callLoader: boolean | LoaderConfig = true, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<Entity>> {
        return this._httpService.get<Entity>(`${environment.apiUrl}Entities/${this._user.userCountryCode}/${id}`, callLoader, undefined, undefined, errorConfig);
    }

    getEntityByTin(tin: string, countryCode?: string, callLoader: boolean | LoaderConfig = true, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<EntityExistenceAndPermissions>> {
        return this._httpService.get<EntityExistenceAndPermissions>(`${environment.apiUrl}Entities/${countryCode ?? this._user.userCountryCode}/TIN/${tin}/CheckExistenceAndPermissions`, callLoader, null, new RequestCacheConfig(30, undefined, 'seconds'), errorConfig);
    }

    postEntity(body: CreateEntity, callLoader: boolean | LoaderConfig = true, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<string>> {
        return this._httpService.post(`${environment.apiUrl}Entities/${this._user.userCountryCode}`, body, undefined, callLoader, undefined, errorConfig);
    }

    getLegalRepresentatives(entityId: string, body?: ISAPI, callLoader: boolean | LoaderConfig = true, errorConfig?: ErrorConfig | boolean){
        if (!body) {
            body = new ISAPI([], new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}Entities/${this._user.userCountryCode}/${entityId}/LegalRepresentatives/Get`, body, undefined, callLoader, undefined, errorConfig);
    }
 
    getUserEntityData(userEmail: string): Observable<any> {
        let cacheConfig = new RequestCacheConfig(9999, this._auth.tokenIsExpiring$, 'hours')

        return this._user.getCurrentUserClaims(userEmail, true, cacheConfig).pipe(
          catchError((err) => { throw err }),
          concatMap( (sr_userClaims: ServerResponse<any>) => {
            let userCountryId = sr_userClaims.data.claims.find( (claim: any) => claim.claimType == 'CountryId' )?.claimValue;
            if (String.isNullOrUndefinedOrWhitespace(userCountryId)) 
              throw new Error('INVALID_USER_CLAIMS');
            
            return this._user.getUserCountryData(userCountryId).pipe(
              catchError((err) => { throw err }),
              concatMap( (sr_country: ServerResponse<any>) => {
                if (!sr_country || !sr_country.data)
                  throw new Error('INVALID_USER_COUNTRY');
    
                return this._user.getCurrentUser(sr_country.data.twoLetterIsoCode).pipe(
                  catchError((err) => { throw err }),
                  concatMap( (sr_currentUser: ServerResponse<UserProfile>) => {
                    if (!sr_currentUser || !sr_currentUser.data) 
                      throw new Error('INVALID_USER');
                    
                    if (!sr_currentUser.data.tin) {
                        return of({ user: sr_currentUser.data, entity: null, claims: sr_userClaims.data, country: sr_country.data })
                    } else {
                        return this.getEntityByTin(sr_currentUser.data.tin, sr_country.data.twoLetterIsoCode).pipe(
                            catchError((err) => EMPTY),
                            map( (sr_userEntity: ServerResponse<EntityExistenceAndPermissions> | undefined) => ({ user: sr_currentUser.data, entity: sr_userEntity?.data, claims: sr_userClaims.data, country: sr_country.data }))
                        )
                    }
                  })
                )
              })
            )
          })
        )
    }
}